/**
 * The letter as a row in somebody's inbox.
 *
 * Three fields on this tab decide whether a message is opened at all - sender
 * name, subject, preheader - and they are edited as three unrelated text inputs
 * that never appear together anywhere. This puts them where the reader sees
 * them: one line, in the order and the truncation a mail client applies.
 *
 * The subject counter is not a limit, it is the width of the column: most
 * clients show roughly 45 characters on a phone and 70 on a desktop, and a
 * subject whose point arrives at character 90 has no point.
 */
import { Mail } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../../i18n/I18nProvider";

/** Roughly where a phone stops showing a subject line. */
const MOBILE_SUBJECT = 45;
/** Roughly where a desktop client stops. */
const DESKTOP_SUBJECT = 70;

/**
 * Takes the three fields rather than a campaign: the template editor shows the
 * same row and has no campaign to hand it.
 *
 * An ABSENT `fromName` drops the sender line entirely, which is not the same as
 * an empty one. A template genuinely has no sender - the campaign decides that
 * later - so telling its author the sender name is missing would be reporting a
 * gap they cannot fill on the screen they are on.
 */
export function InboxPreview({
  fromName,
  subject: rawSubject,
  preheader,
}: {
  fromName?: string;
  subject: string;
  preheader: string;
}) {
  const t = useT();
  const subject = rawSubject.trim();
  const len = subject.length;

  return (
    <div className="space-y-2">
      <div className="rounded-xl border border-border bg-background/60 p-3.5">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Mail className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            {fromName !== undefined && (
              <div className="truncate text-sm font-semibold">
                {fromName.trim() || (
                  <span className="font-normal text-muted-foreground">{t("marketing.message.noFromName")}</span>
                )}
              </div>
            )}
            <div className={cn("mt-0.5 truncate text-sm", !subject && "text-muted-foreground")}>
              {subject || t("marketing.message.noSubject")}
            </div>
            <div className="mt-0.5 truncate text-xs text-muted-foreground/80">
              {preheader.trim() || t("marketing.message.noPreheader")}
            </div>
          </div>
        </div>
      </div>

      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span
          className={cn(
            "font-mono tabular-nums",
            len > DESKTOP_SUBJECT && "text-amber-600 dark:text-amber-400",
          )}
        >
          {t("marketing.message.subjectLength", { n: len })}
        </span>
        <span className="text-muted-foreground/70">
          {len > DESKTOP_SUBJECT
            ? t("marketing.message.subjectTooLong", { desktop: DESKTOP_SUBJECT })
            : t("marketing.message.subjectFits", { mobile: MOBILE_SUBJECT })}
        </span>
      </p>
    </div>
  );
}
